import React from "react";
import moment from "moment";
import CardClass from "../../../CardClass";
import MUIListItemIcon from "@mui/material/ListItemIcon";
import MUIFileDownloadIcon from "@mui/icons-material/FileDownload";
import MUICloseIcon from "@mui/icons-material/Close";
import MUIMenuItem from "@mui/material/MenuItem";
import MUIAvatar from "@mui/material/Avatar";
import MUITypography from "@mui/material/Typography";
import MUIBox from "@mui/material/Box";
import MUIStack from "@mui/material/Stack";
import Progress from "../../../Progress";
import DataGrid from "../../../DataGrid";
import EmptyState from "../../../EmptyState";
import OrderStatusReason from "../../../../Utility/Crud/OrderStatusReason";
import Exporter from "../../../../Utility/Exporter";
import Menu from "../../../Menu";
import Chip from "../../../Chip";
import MainContext from "../../../../Context/MainContext/MainContext";

export default class CardReportOrderTimelineAuditResults extends CardClass {
  static contextType = MainContext; // For legacy class components

  renderContent() {
    if (!this.props.isLoading) {
      if (this.props.rows && this.props.rows.length > 0) {
        // Ensure filtering is always an array
        const filtering = Array.isArray(this.props.filtering)
          ? this.props.filtering
          : [];

        // Extract arrays of new and old reason IDs from filtering objects
        const newValueIds = filtering.map((filter) => filter.id);
        const oldValueIds = filtering.map((filter) => filter.oldId);

        // Filter rows to match both new and old reason IDs
        // Only include rows where both IDs are present in the filter arrays
        const filteredRows =
          newValueIds.length > 0 && oldValueIds.length > 0
            ? this.props.rows.filter(
                (row) =>
                  newValueIds.includes(row["audit_log.new_value_id"]) &&
                  oldValueIds.includes(row["audit_log.old_value_id"]),
              )
            : this.props.rows;

        // Render chips for each filter
        return (
          <MUIStack spacing={2}>
            {filtering.length > 0 && (
              <MUIBox
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 1,
                  background: (theme) => theme.palette.background.default,
                  padding: 1,
                  borderRadius: 1,
                }}
              >
                <MUITypography variant="subtitle2">Routes</MUITypography>
                <MUIBox sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {filtering.map((filter, idx) => (
                    <Chip
                      key={`${filter.id}-${filter.oldId}` || idx}
                      label={`${filter.oldReasonName} ➔ ${filter.newReasonName}`}
                      title={`${filter.oldReasonName} ➔ ${filter.newReasonName}`}
                      avatar={
                        <MUIAvatar sx={{ bgcolor: filter.avatarColor }}>
                          {OrderStatusReason.getIcon({
                            fontSize: "small",
                            sx: { color: filter.iconColor },
                          })}
                        </MUIAvatar>
                      }
                      orderStatusReason={
                        this.context.orderStatusReasonsIndexed[filter.id] ??
                        null
                      }
                      onSettingsChange={this.props.onSettingsChange}
                      onDelete={() => {
                        this.props.onDelete(filter);
                      }}
                      deleteIcon={<MUICloseIcon />}
                    />
                  ))}
                </MUIBox>
              </MUIBox>
            )}
            <MUIBox>
              <DataGrid
                rows={filteredRows}
                onClick={this.handleClickRow.bind(this)}
                columns={this.props.columns}
                defaultColumnWidths={this.props.defaultColumnWidths}
                enableGroupingPanel={true}
                onGroupingChange={this.props.onGroupingChange}
                grouping={this.props.grouping}
              />
            </MUIBox>
          </MUIStack>
        );
      } else {
        return (
          <EmptyState
            line1={
              this.props.loadedAt ? "No results found" : "Waiting for results"
            }
            line2={
              this.props.loadedAt
                ? "Try adjusting your settings"
                : "Please select your settings"
            }
          />
        );
      }
    }
    return <Progress message="Loading Data..." />;
  }

  /**
   * Handle clicking on a grid row.
   */
  handleClickRow(row) {
    this.openOrder(row);
  }

  /**
   * Opens the order.
   *
   * @param {Object} row The grid row.
   */
  openOrder(row) {
    window.open(
      `/chart/${row["audit_log.order_activity.patient_id"]}/order/${row["audit_log.row_id"]}`,
      "_blank",
    );

    this.setState({
      queueDirty: true,
    });
  }

  /**
   * @returns {string} The title of the card.
   */
  getTitle() {
    return "Results";
  }

  getActions() {
    const handleExport = () => {
      const csvRows = [];

      //Table headers
      csvRows.push(this.props.columns.map((column) => column.title));

      // Table rows
      const rows = this.props.rows;
      rows.forEach((row) => {
        const csvRow = [];
        this.props.columns.forEach((column) => {
          if (column.name === "audit_log.timestamp") {
            csvRow.push(
              row[column.name]
                ? moment
                    .utc(row[column.name])
                    .local()
                    .format("YYYY-MM-DD h:mma")
                : null,
            );
          } else {
            csvRow.push(row[column.name]);
          }
        });
        csvRows.push(csvRow);
      });

      const filename = `Order Timeline Audit - ${moment(
        this.props.loadedAt,
      ).format("YYYY-MM-DD h mm a")}.csv`;

      Exporter.exportCSV(csvRows, filename);
    };
    return [
      <Menu
        menuItems={[
          <MUIMenuItem
            disabled={
              this.props.isLoading ||
              !this.props.rows ||
              (this.props.rows && this.props.rows.length === 0)
            }
            onClick={handleExport}
          >
            <MUIListItemIcon>
              <MUIFileDownloadIcon fontSize="small" />
            </MUIListItemIcon>
            Export CSV
          </MUIMenuItem>,
        ]}
      ></Menu>,
    ];
  }
}
